import React from 'react';
import { useTranslation } from 'react-i18next';
import './TableWidget.css';

// Libellés KPI renvoyés par le backend (en anglais) → clés i18n.
// ⚠️ Le mapping se fait par ÉGALITÉ DE CHAÎNE : renommer un libellé dans
// analyticsQueryCosmos.js sans l'ajouter ici fait silencieusement retomber la ligne sur
// l'anglais. Les deux fichiers doivent être modifiés ensemble.
const KPI_LABEL_TO_I18N = {
    'Total Calls': 'KPI_TOTAL_CALLS',
    'Serviced Calls': 'KPI_SERVICED_CALLS',
    'Abandon Calls': 'KPI_ABANDON_CALLS',
    'Quick Drop': 'KPI_QUICK_DROP',
    'IVR Abandons': 'KPI_IVR_ABANDONS',
    'Redirected Calls': 'KPI_REDIRECTED_CALLS',
    // Anciennement « Service Level (< 30s) » : le seuil vient désormais du profil analytics,
    // le libellé ne peut donc plus l'annoncer en dur.
    'Service Level': 'KPI_SERVICE_LEVEL',
    'Answer Rate': 'KPI_ANSWER_RATE',
    'Abandon Rate': 'KPI_ABANDON_RATE',
    'Average Talking Time': 'KPI_AVG_TALKING_TIME',
    'Maximum Talking Time': 'KPI_MAX_TALKING_TIME',
    'Average Time of Ringing': 'KPI_AVG_RINGING_TIME',
    'Average Queue Time': 'KPI_AVG_QUEUE_TIME',
    'Maximum Queue Time': 'KPI_MAX_QUEUE_TIME',
    // Causes de débordement (widget overflow-by-reason / Report-4)
    'No agents Logged In': 'OVERFLOW_CAUSE_NO_AGENTS',
    'Time out': 'OVERFLOW_CAUSE_TIMEOUT',
    'max call in the queue reached': 'OVERFLOW_CAUSE_QUEUE_FULL',
    'Calls Out of Business Time': 'OVERFLOW_CAUSE_OUT_OF_HOURS',
    'Calls during Bank Holiday': 'OVERFLOW_CAUSE_BANK_HOLIDAY'
};

// Maps table column KEYS (data field names) to i18n keys, for auto-generated columns
// (row-based tables like the daily half-hour breakdown). Header falls back to the raw key.
const COLUMN_KEY_TO_I18N = {
    slot: 'DAILY_HOURS',
    overflowInQueue: 'DAILY_OVERFLOW_QUEUE',
    overflowRingAgent: 'DAILY_OVERFLOW_AGENT',
    abandons: 'DAILY_ABANDONS',
    servedWithoutQueue: 'DAILY_SERVED_NO_QUEUE',
    receivedOpenState: 'DAILY_RECEIVED_OPEN'
};

const TableWidget = ({ data, config }) => {
    const { t } = useTranslation();

    const translateKpiLabel = (value) => {
        const key = KPI_LABEL_TO_I18N[value];
        return key ? t(key) : value;
    };

    // Round any decimal to max 1 digit. Integers stay clean.
    const round1 = (n) => Number.isInteger(n) ? n : Math.round(n * 10) / 10;

    // Format value based on column format. All decimals capped at 1 digit.
    const formatValue = (value, format) => {
        if (value === null || value === undefined) return '-';

        // Pas de format spécifié + valeur string → retourner tel quel
        // (utilisé pour les colonnes label comme "Time Range" avec des valeurs "0-15s").
        if (!format && typeof value === 'string') return value;

        const n = Number(value);
        if (!isFinite(n)) {
            // Garder la string brute si on ne peut pas la convertir en nombre
            return typeof value === 'string' ? value : '-';
        }

        switch (format) {
            case 'duration': {
                if (n < 0) return '-';
                if (n > 86400) return '—'; // garde-fou bad backend value
                const minutes = Math.floor(n / 60);
                const seconds = Math.floor(n % 60);
                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
            }
            // Durée SANS plafond de 24 h, en `hh:mm:ss` — voir MetricWidget pour le pourquoi :
            // les durées du canal mail se comptent en jours, pas en minutes de conversation.
            case 'longDuration': {
                if (n < 0) return '-';
                const total = Math.floor(n);
                const pad = (v) => String(v).padStart(2, '0');
                return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
            }
            // Époque en SECONDES rendue en heure locale 24 h — le pendant à l'écran de ce que
            // `Utils/excelExport.js` fait pour les colonnes `ts_`. Sans ce cas, un champ `ts_`
            // tombait dans le défaut numérique et s'affichait « 1 788 352 246 ». C'est le même
            // mode de défaillance silencieuse que la convention de préfixes vise à éviter, mais
            // à l'écran plutôt qu'au fichier.
            case 'clock': {
                if (!n) return '-';
                return new Date(n * 1000).toLocaleTimeString([], {
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                });
            }
            case 'percentage':
                return `${round1(n)}%`;
            case 'number':
                return round1(n).toLocaleString();
            default:
                return round1(n).toLocaleString();
        }
    };

    // Vérifier que data est un tableau valide
    if (!Array.isArray(data) || data.length === 0) {
        return null; // Le composant parent Widget affichera l'état vide
    }

    // Auto-generate columns from data keys if not provided
    const configColumns = config?.config?.columns || config?.columns;
    let columns;

    if (configColumns) {
        columns = configColumns;
    } else {
        // Generate columns dynamically from first data row
        const firstRow = data[0];

        // Detect the label column: 'kpi', 'range', ou 'from' (matrice de débordement :
        // lignes = file d'origine, colonnes = file de destination)
        const labelKey = firstRow.hasOwnProperty('kpi') ? 'kpi' :
            firstRow.hasOwnProperty('range') ? 'range' :
                firstRow.hasOwnProperty('from') ? 'from' :
                    Object.keys(firstRow)[0]; // fallback to first key

        // Label column SANS width explicite → prend l'espace restant (flex).
        // Colonnes data avec width: 100px → tailles prédictibles. Si total > widget,
        // scroll horizontal géré par .analytics-table-container.
        const labelColumn = {
            key: labelKey,
            nameKey: COLUMN_KEY_TO_I18N[labelKey],
            name: labelKey === 'kpi' ? t('KPI')
                : labelKey === 'range' ? t('TIME_RANGE')
                    : labelKey === 'from' ? t('QUEUE')
                        : labelKey
        };

        // `labelKey` est exclu explicitement : sans ça, une colonne de libellé non listée dans
        // `metadataKeys` (cas de 'from') était rendue DEUX fois — en tête puis en colonne data.
        const metadataKeys = ['kpi', 'range', 'format', 'agentName', 'flowName', 'queueName', 'agentId', 'flow', 'queue'];
        const dataKeys = Object.keys(firstRow).filter(key => key !== labelKey && !metadataKeys.includes(key));

        columns = [
            labelColumn,
            ...dataKeys.map(key => ({ key: key, nameKey: COLUMN_KEY_TO_I18N[key], name: key, width: 100 }))
        ];
    }

    return (
        <div className="table-widget">
            <div className="analytics-table-container">
                <table>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                                    {col.nameKey ? t(col.nameKey) : col.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => {
                            // Get row-level format if it exists
                            const rowFormat = row.format;

                            return (
                                <tr key={idx}>
                                    {columns.map(col => {
                                        // Never apply row-level format to label columns (kpi, range) - they're text, not metrics
                                        const isLabelColumn = col.key === 'kpi' || col.key === 'range';
                                        const cellFormat = isLabelColumn ? undefined : (col.format || rowFormat);

                                        return (
                                            <td key={col.key}>
                                                {col.formatter
                                                    ? col.formatter(row[col.key])
                                                    : col.key === 'kpi'
                                                        ? translateKpiLabel(row[col.key])
                                                        : formatValue(row[col.key], cellFormat)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TableWidget;
